import { ICAO_TO_ISO2 } from './mrz-country-map';
import { logClientError, OcrContext } from './convex';

// ===== Types =====

export interface OcrFieldMapping {
  wizardFieldId: string;
  displayLabel: string;
  transform?: (raw: any) => any;
}

export interface DocumentOcrConfig {
  documentType: string;
  prompt: string;
  fieldMapping: Record<string, OcrFieldMapping>;
  /** French description of the expected document, e.g. "un passeport". Used to verify the upload. */
  expectedDocumentType?: string;
  /** Set true for documents that belong to another person (spouse, guarantor, etc.) — skips name verification. */
  skipNameVerification?: boolean;
  /** Firm-defined extra criteria the document must meet (e.g. "solde visible + relevé < 30 jours"). When set, Gemini returns a discrete meets_requirements verdict. */
  verificationCriteria?: string;
  /**
   * Ce fichier est l'un de plusieurs déposés pour la même exigence.
   *
   * Les critères du cabinet décrivent un ENSEMBLE — « deux photos conformes »,
   * « les relevés des trois derniers mois ». Examinés fichier par fichier, ils
   * échouent sur chacun : une cliente qui a déposé ses deux photos s'est fait
   * répondre deux fois « vous n'avez fourni qu'une seule photo », pendant que
   * l'écran affichait « 2 documents téléchargés » juste en dessous.
   *
   * On dit donc au modèle de ne juger que ce que CE fichier montre, et de
   * laisser la quantité tranquille : elle se compte dans l'écran, pas dans
   * l'image.
   */
  partOfSet?: boolean;
}

export interface DocumentOcrResult {
  success: boolean;
  extractedFields: Record<string, any>;
  displayFields: { label: string; value: string; fieldId: string }[];
  error?: string;
  wrongDocumentFeedback?: string;
  nameMismatchFeedback?: string;
  /** Ce que le cabinet doit savoir, sans que le client soit arrêté. */
  observations?: string;
  requirementsFeedback?: string;
}

// ===== Shared helpers =====

export function parseDate(str: string | undefined | null): Date | null {
  if (!str) return null;
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
}

export function icaoToIso2(code: string | undefined | null): string | null {
  if (!code) return null;
  return ICAO_TO_ISO2[code.toUpperCase()] || null;
}

export function mapGender(v: string | undefined | null): string | null {
  if (!v) return null;
  const map: Record<string, string> = { M: 'masculin', F: 'feminin' };
  return map[v.toUpperCase()] || null;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1]);
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

function formatDisplayValue(value: any): string {
  if (value instanceof Date) {
    const months = [
      'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
      'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
    ];
    return `${value.getDate()} ${months[value.getMonth()]} ${value.getFullYear()}`;
  }
  if (typeof value === 'string') return value;
  return String(value);
}

// clientName is user-controlled, so it must NEVER be interpolated into a Gemini
// instruction. We ask Gemini to extract the name from the document and compare
// in JS, removing the LLM from the trust path for name verification.
function normalizeName(s: string): string {
  return s
    .normalize('NFKD')
    .replace(/\p{Mn}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .sort()
    .join(' ');
}

export function namesMatch(extracted: string | null | undefined, expected: string): boolean {
  // No name on document → accept (matches prior lenient behavior for certificates etc.)
  if (!extracted || !extracted.trim()) return true;
  const extractedNorm = normalizeName(extracted);
  const expectedNorm = normalizeName(expected);
  if (!expectedNorm) return true;
  if (extractedNorm === expectedNorm) return true;
  const extractedTokens = new Set(extractedNorm.split(' '));
  return expectedNorm.split(' ').every((t) => extractedTokens.has(t));
}

function buildMismatchFeedback(extracted: string | null | undefined, expected: string): string {
  if (!extracted || !extracted.trim()) {
    return `Aucun nom n'a été détecté sur le document. Le nom attendu est « ${expected} ». Veuillez vérifier que vous avez téléversé le bon document.`;
  }
  return `Le nom sur le document (« ${extracted} ») ne correspond pas au nom attendu (« ${expected} »). Veuillez vérifier que vous avez téléversé le bon document.`;
}

// ===== Main scan function =====

import { callGeminiWithImage } from "./gemini-client";
import { shrinkImage } from "./shrinkImage";

/** Les seuls verdicts qui parlent du DOCUMENT lui-même. Le reste est une panne. */
const CODES_METIER = new Set(['WRONG_DOCUMENT', 'REQUIREMENTS_NOT_MET', 'NAME_MISMATCH']);

/**
 * La lecture a-t-elle échoué pour une raison technique ?
 *
 * Distinction qui manquait : tout ce qui n'était pas un verdict métier était
 * traité comme « vérifié ». Un document trop volumineux, un réseau coupé ou un
 * proxy en erreur affichaient donc une coche verte au client, et s'enregistraient
 * comme vérifiés, alors que Formio n'avait rien lu du tout. Ne pas bloquer
 * quelqu'un parce que NOTRE lecture a échoué est une bonne intention ; prétendre
 * l'avoir vérifié en est une autre.
 */
export function estEchecTechnique(r: DocumentOcrResult): boolean {
  return !r.success && !(r.error && CODES_METIER.has(r.error));
}

export async function scanDocument(
  file: File,
  config: DocumentOcrConfig,
  onProgress?: (progress: number) => void,
  ctx?: OcrContext,
  clientName?: string,
  language: string = "fr",
): Promise<DocumentOcrResult> {
  try {
    onProgress?.(10);
    // Client-facing feedback must be written in the client's language.
    const feedbackLang =
      language === "en" ? "English" : language === "es" ? "Spanish" : "French";

    // Réduire AVANT d'encoder : une photo de téléphone dépasse à elle seule la
    // limite du proxy une fois encodée, et la lecture échouait chez le client.
    const pret = await shrinkImage(file);
    const base64 = await fileToBase64(pret);
    onProgress?.(30);

    const verificationSuffix = config.expectedDocumentType
      ? `\n\nIMPORTANT: Verify that the uploaded document matches what was requested.\nThe client was asked: "${config.expectedDocumentType}"\nThey uploaded this document image.\n\nAdd these two fields to your JSON:\n  "is_correct_document": true if the uploaded document is a reasonable response to what was asked — false only if the document is clearly unrelated (e.g. a passport when a diploma was requested, or a random photo instead of an official document)\n  "wrong_document_feedback": if false, write 1-2 sentences in ${feedbackLang} addressed directly to the client. Explain what document they uploaded, why it does not match what was requested, and what they need to upload instead. This message will be shown to the client directly so be clear and helpful. null if correct.\n\nBe lenient: if the document is roughly the right type, accept it. Only reject if it is clearly the wrong kind of document.`
      : '';

    // Security: do NOT interpolate clientName here — it is user-controlled and
    // could carry prompt-injection payloads. Ask Gemini to extract the name
    // verbatim; we compare deterministically in JS below (namesMatch).
    const nameVerificationSuffix =
      clientName && !config.skipNameVerification
        ? `\n\nAlso extract the name as written on this document. Add this field to your JSON:\n  "name_on_document": the person's full name as it appears on the document (string), or null if this document does not show a person's name`
        : '';

    // Firm-defined criteria → discrete verdict. Distinct from the (lenient)
    // document-TYPE check above: these are specific requirements the firm set
    // (e.g. "balance visible + statement dated < 30 days") that must actually
    // be met, not just "is this roughly the right kind of document".
    // Anchor the model to the real current date — it has no inherent notion of
    // "today" (its knowledge cutoff predates the real date), so date-relative
    // criteria like "dated less than 30 days ago" fail without this reference.
    const todayISO = new Date().toISOString().slice(0, 10);
    // Un fichier d'un dépôt groupé ne porte pas l'exigence entière : sans ce
    // rappel, chaque photo d'une paire se voit reprocher d'être seule.
    const noteEnsemble = config.partOfSet
      ? `\n\nIMPORTANT — THIS FILE IS ONE OF SEVERAL uploaded for the same requirement. The other files are sent separately, one at a time, and you cannot see them. Judge ONLY what THIS file itself shows. NEVER report that a required QUANTITY is missing (e.g. "only one photo was provided", "two of the three monthly statements are missing") — the count is handled by the application, not by you. Set meets_requirements to false only if THIS file, on its own, fails a requirement that applies to an individual file.`
      : '';
    // Ne pas pouvoir vérifier n'est pas échouer.
    //
    // Les instructions d'un cabinet servent deux métiers à la fois : elles
    // disent au client quoi faire, et elles servent de critère au modèle. Or
    // « inscrire au dos le nom et la date de naissance » est une consigne
    // parfaite et un critère impossible — on demande une photo du recto, le
    // verso ne sera jamais dans le cadre. Le modèle ne pouvait donc que
    // répondre « l'information au dos n'est pas visible », pour tous les
    // clients, indéfiniment. Même chose pour le passeport : on montre la page
    // de la photo, et la consigne demande de confirmer qu'une AUTRE page est
    // vierge.
    //
    // Un signalement qui se déclenche toujours finit par être ignoré, et ce
    // jour-là le vrai — le permis expiré — passe avec les autres. C'est le
    // signal qu'on protège ici.
    const regleDuVisible = config.verificationCriteria
      ? `\n\nWHAT YOU CAN AND CANNOT ASSESS. Judge ONLY what is actually visible in the image(s) you were given. Some requirements above describe things that are physically outside the frame: what is handwritten on the BACK of a photo or document, a page that was not photographed, another page of the same passport, or a separate document. For any such requirement, you CANNOT assess it — treat it as not applicable and do NOT hold it against the document. Being unable to confirm a requirement is NOT the same as the requirement being unmet: set meets_requirements to false ONLY when what you can actually see visibly contradicts a requirement (for example a date that is clearly expired, or a document that is plainly of the wrong kind). When in doubt, pass.`
      : '';
    const criteriaSuffix = config.verificationCriteria
      ? `\n\nADDITIONAL REQUIREMENTS FROM THE LAW FIRM: this document must satisfy the following specific requirements:\n"${config.verificationCriteria}"\n\nCRITICAL DATE RULE: The current date is exactly ${todayISO} (ISO YYYY-MM-DD). This is an authoritative fact — IGNORE your own sense of what year it is; do NOT assume ${todayISO.slice(0, 4)} is in the future. When a requirement involves recency (e.g. "less than 30 days old"), follow these steps:\n  1. Read the date printed on the document.\n  2. Compute the number of days between that date and ${todayISO}.\n  3. A document dated ${todayISO} itself is 0 days old and is RECENT. Example: a document dated ${todayISO} satisfies "less than 30 days old". A document dated 4 months before ${todayISO} does NOT.\n\nAdd these fields to your JSON:\n  "document_date": the date printed on the document in ISO YYYY-MM-DD, or null if none is visible\n  "document_age_days": integer number of days between document_date and ${todayISO} (0 if same day), or null\n  "meets_requirements": true only if the document clearly satisfies ALL of the requirements above (using document_age_days for any recency rule); false if any requirement is not met or cannot be confirmed from the document\n  "requirements_feedback": if false, write 1-2 sentences in ${feedbackLang} addressed directly to the client explaining which requirement is not met and what they need to upload instead. null if true.`
      : '';

    // La consigne d'un document énumère souvent des « motifs de refus » tirés
    // des règles d'immigration : établissement hors Québec, diplôme absent de
    // la liste admissible, pièce manquante. Ce sont des questions de DROIT, et
    // les trancher n'est pas notre rôle : l'avocat le fait, sur pièces. Sans ce
    // suffixe, le modèle lit « motifs de refus » juste avant qu'on lui demande
    // si c'est le bon document, et il refuse un vrai diplôme parce qu'il vient
    // d'Ontario. Le client se ferait alors renvoyer par une machine sur un
    // jugement qu'elle n'a pas à porter, et le cabinet ne verrait jamais le
    // dossier.
    //
    // Donc : le refus ne porte que sur la NATURE de la pièce. Tout le reste
    // remonte en observation, exactement comme le nom qui ne correspond pas,
    // et le cabinet tranche.
    const observationsSuffix = `\n\nSÉPARATION IMPORTANTE. Les motifs de refus ou conditions d'admissibilité énumérés plus haut ne doivent JAMAIS te faire répondre false à "is_correct_document". Ce champ ne juge que la NATURE du document : est-ce bien une pièce de ce genre, oui ou non. Un diplôme reste un diplôme même s'il vient d'une province qui ne convient pas ; un relevé bancaire reste un relevé bancaire même s'il est trop ancien.\n\nAjoute ce champ à ton JSON :\n  "observations": en FRANÇAIS, une phrase courte pour le cabinet si un point énuméré plus haut n'est pas satisfait, ou si le document semble tronqué ou illisible. Dis ce que tu constates, sans conclure sur l'admissibilité. Exemple : « Diplôme délivré en Ontario, pas au Québec. » null si tout est conforme.`;

    const text = await callGeminiWithImage(
      config.prompt + verificationSuffix + nameVerificationSuffix + criteriaSuffix + regleDuVisible + noteEnsemble + observationsSuffix,
      base64,
      // Le type du fichier RÉDUIT : une image convertie en JPEG déclarée PNG
      // ferait rejeter la requête par le modèle.
      pret.type,
    );

    onProgress?.(80);

    if (!text) {
      return { success: false, extractedFields: {}, displayFields: [], error: 'NO_RESPONSE' };
    }

    const jsonStr = text.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
    let parsed;
    try {
      parsed = JSON.parse(jsonStr);
    } catch {
      console.error('Failed to parse Gemini response:', text);
      logClientError('document-ocr:parseResponse', new Error('Failed to parse Gemini response'));
      return { success: false, extractedFields: {}, displayFields: [], error: 'PARSE_ERROR' };
    }

    onProgress?.(100);

    // Log full Gemini response for debugging
    if (config.expectedDocumentType) {
      console.log('[OCR verification]', config.documentType, {
        expectedDocumentType: config.expectedDocumentType,
        is_correct_document: parsed.is_correct_document,
        wrong_document_feedback: parsed.wrong_document_feedback,
        rawParsed: parsed,
      });
    }

    // If document type verification is enabled and failed, return early
    if (config.expectedDocumentType && parsed.is_correct_document === false) {
      return {
        success: false,
        extractedFields: {},
        displayFields: [],
        error: 'WRONG_DOCUMENT',
        wrongDocumentFeedback: parsed.wrong_document_feedback ?? undefined,
      };
    }

    // Name verification: deterministic comparison in JS so user-controlled
    // clientName cannot influence the verification outcome via prompt injection.
    if (
      clientName &&
      !config.skipNameVerification &&
      !namesMatch(parsed.name_on_document, clientName)
    ) {
      return {
        success: false,
        extractedFields: {},
        displayFields: [],
        error: 'NAME_MISMATCH',
        nameMismatchFeedback: buildMismatchFeedback(parsed.name_on_document, clientName),
      };
    }

    // Apply field mappings
    //
    // Fait AVANT le verdict sur les critères du cabinet, et non après : un
    // document recalé sur les critères reste le bon type de document, celui de
    // la bonne personne, et il a été lu correctement. Un passeport à qui il
    // manque les pages de tampons d'entrée porte quand même un nom, une date de
    // naissance et un numéro justes. Repartir les mains vides obligeait le
    // client à ressaisir onze champs que Formio venait de lire sous ses yeux.
    const extractedFields: Record<string, any> = {};
    const displayFields: { label: string; value: string; fieldId: string }[] = [];

    for (const [jsonKey, mapping] of Object.entries(config.fieldMapping)) {
      const rawValue = parsed[jsonKey];
      if (rawValue == null) continue;

      const value = mapping.transform ? mapping.transform(rawValue) : rawValue;
      if (value == null) continue;

      extractedFields[mapping.wizardFieldId] = value;
      displayFields.push({
        label: mapping.displayLabel,
        value: formatDisplayValue(value),
        fieldId: mapping.wizardFieldId,
      });
    }

    // Firm-criteria verdict: reject when the document doesn't meet the firm's
    // specific requirements (evaluated after the type check so type feedback
    // takes priority). Blocks + lets the client retry; useDocumentOcr accepts it
    // after MAX_ATTEMPTS so no one is permanently stuck.
    //
    // Le refus porte sur le DOCUMENT, pas sur ce qui en a été lu : les champs
    // extraits repartent avec le verdict. `WRONG_DOCUMENT` et `NAME_MISMATCH`
    // au-dessus continuent, eux, de ne rien renvoyer — un autre document ou le
    // document de quelqu'un d'autre n'a rien à préremplir.
    if (config.verificationCriteria && parsed.meets_requirements === false) {
      return {
        success: false,
        extractedFields,
        displayFields,
        error: 'REQUIREMENTS_NOT_MET',
        requirementsFeedback: parsed.requirements_feedback ?? undefined,
      };
    }

    // Rattachée ici, après tous les verdicts : une observation n'en est pas un.
    const observations =
      typeof parsed.observations === "string" && parsed.observations.trim()
        ? parsed.observations.trim()
        : undefined;

    const hasFields = Object.keys(extractedFields).length > 0;
    // Verification-only configs (empty fieldMapping) succeed when the document passed type check
    const isVerificationOnly = Object.keys(config.fieldMapping).length === 0;

    return {
      success: hasFields || isVerificationOnly,
      extractedFields,
      displayFields,
      error: hasFields ? undefined : 'NO_FIELDS_EXTRACTED',
      observations,
    };
  } catch (err) {
    console.error('Document scan error:', err);
    // Le type de document, sans quoi l'alerte Discord ne dit pas lequel a
    // échoué et il faut deviner — ce qui nous est arrivé.
    logClientError('document-ocr:scan', err, {
      documentType: config.documentType,
      expected: config.expectedDocumentType,
      fileSizeKo: Math.round(file.size / 1024),
      fileType: file.type,
      submissionId: ctx?.submissionId,
    });
    return {
      success: false,
      extractedFields: {},
      displayFields: [],
      error: err instanceof Error ? err.message : 'UNKNOWN_ERROR',
    };
  }
}
